import { getHostbaseUrl } from '@core/tools';
import { EAxiosResponseCode, getHttpErrorMessage, Http } from '@core/api';
import { ETimeRange } from '@core/main/types/home';
import {
  IDistributionTaskCompanyDetail, IIncomeDetailParams, IAgentEstimateRes, 
  IAgentTrendRes, ICarouselListResponse, ICarouselUpdateRequest, 
  ICommonEntryResponse, IDataStatisticsManage, IStatisticsConfigRequest, 
  IStatisticsRankRes, IStatisticsResponse, 
} from '@core/main/types';
import { IDistributionSettlement } from '@core/main/types/crowd-source/commission-share';

const hostUrl = getHostbaseUrl();

/**
 * 首页 - 常用入口
 */
export async function queryCommonEntranceList() {
  let obj = {} as ICommonEntryResponse;

  try {
    const { code, context, message } = await Http.get<ICommonEntryResponse>(`${hostUrl}/iam/homepage/common-entry/list`);

    if (code === EAxiosResponseCode.Succeed) {
      obj = context || {} as ICommonEntryResponse;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return obj;
}

/**
 * 首页 - 常用入口 - 配置
 */
export async function updateCommonEntranceList(ids: number[]) {
  try {
    const { code, message } = await Http.post<ICommonEntryResponse>(`${hostUrl}/iam/homepage/common-entry/config`, { ids });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 首页 - 轮播图列表
 */
export async function queryHomePageCarousel() {
  let data = [] as ICarouselListResponse[];

  try {
    const { code, context, message } = await Http.get<ICarouselListResponse[]>(`${hostUrl}/iam/homepage/carousel/list`);

    if (code === EAxiosResponseCode.Succeed) {
      data = context || [] as ICarouselListResponse[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 基础配置 - 轮播图配置更新
 */
export async function updateCarouselImageConfig(params: ICarouselUpdateRequest) {

  try {
    const { code, message } = await Http.post(`${hostUrl}/iam/homepage/carousel/config`, params);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 基础配置 - 轮播图配置清空
 */
export async function clearCarouselImageConfig(id: number) {

  try {
    const { code, message } = await Http.post(`${hostUrl}/iam/homepage/carousel/del`, { id });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 首页 - 统计数据列表
 */
export async function queryHomePageStatistics(timeRange: ETimeRange) {
  let data = {} as IStatisticsResponse;

  try {
    const { code, context, message } = await Http.get<IStatisticsResponse>(`${hostUrl}/pay/homepage/statistics`, { timeRange });

    if (code === EAxiosResponseCode.Succeed) {
      data = context || {} as IStatisticsResponse;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 首页 - 管理 - 统计数据列表
 */
export async function queryHomePageStatisticsManage(businessId: number) {
  let data = {} as IDataStatisticsManage;

  try {
    const { code, context, message } = await Http.get<IDataStatisticsManage>(`${hostUrl}/iam/homepage/statistics/list`, { businessId });

    if (code === EAxiosResponseCode.Succeed) {
      data = context || {} as IDataStatisticsManage;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 首页 - 管理 - 更新统计数据
 */
export async function updateHomePageStatistics(params: IStatisticsConfigRequest) {

  try {
    const { code, message } = await Http.post<void>(`${hostUrl}/iam/homepage/statistics/config`, params);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 首页 - 代理商分润总额排名
 */
export async function queryStatisticsRank() {
  let data = [] as IStatisticsRankRes[];

  try {
    const { code, context, message } = await Http.get<IStatisticsRankRes[]>(`${hostUrl}/report/crowd/statistics/rank`);

    if (code === EAxiosResponseCode.Succeed) {
      data = context || [] as IStatisticsRankRes[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 首页 - 代理商预估收益
 */
export async function queryAgentEstimate() {
  let data = {} as IAgentEstimateRes;

  try {
    const { code, context, message } = await Http.get<IAgentEstimateRes>(`${hostUrl}/report/crowd/statistics/estimate`);

    if (code === EAxiosResponseCode.Succeed) {
      data = context || {} as IAgentEstimateRes;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 首页 - 柱状图统计
 */
export async function queryAgentTrend() {
  let data = {} as IAgentTrendRes;

  try {
    const { code, context, message } = await Http.get<IAgentTrendRes>(`${hostUrl}/report/crowd/statistics/bar`);

    if (code === EAxiosResponseCode.Succeed) {
      data = context || {} as IAgentTrendRes;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 首页 - 订单预估收益明细分页
 */
export async function queryOrderEstimatePage(param: IIncomeDetailParams) {
  let data = [] as IDistributionTaskCompanyDetail[];
  let total = 0;

  try {
    const { code, context, message } = await Http.post<{ data: IDistributionTaskCompanyDetail[]; total: number }>(`${hostUrl}/report/crowd/statistics/estimate/order-page`, param);

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IDistributionTaskCompanyDetail[]; total: number };

      data = items || [];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 首页 - 订单预估收益明细结算单
 */
export async function queryOrderEstimateDetail() {
  let data = [] as IDistributionSettlement[];

  try {
    const { code, context, message } = await Http.post<{ resultList: IDistributionSettlement[] }>(`${hostUrl}/report/crowd/statistics/estimate/order-detail`);

    if (code === EAxiosResponseCode.Succeed) {
      const { resultList: items } = context || {} as { resultList: IDistributionSettlement[] };

      data = items || [];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}